import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { AnnouncementContent } from './AnnouncementModal';
import type { Announcement } from './types';

interface Props {
  /** Catalogue complet, trié date desc (cf. announcements/index.ts). */
  announcements: Announcement[];
  /** Ids déjà lus (depuis cabinet_members.seen_announcements). */
  readIds: ReadonlySet<string>;
  open: boolean;
  onClose: () => void;
  /** Marquer UNE annonce lue (sélection dans la sidebar). */
  onMarkRead: (id: string) => void;
  /** « Tout marquer comme lu ». */
  onMarkAllRead: () => void;
}

/**
 * Centre d'annonces — la modale produit augmentée d'une mini-sidebar.
 *
 * Volet gauche : liste de toutes les annonces du catalogue (lues et non lues),
 * pastille or sur les non-lues, item actif surligné. Volet droit : le contenu
 * de l'annonce sélectionnée (`AnnouncementContent`, rendu identique à la
 * modale simple — digest ou spotlight selon l'annonce).
 *
 * Sélectionner une annonce la marque lue (fire-and-forget côté appelant,
 * même pattern que la modale login). 100 % présentielle : l'état lu/non-lu
 * vient des props, aucune donnée fetchée ici.
 */
export function AnnouncementCenterModal({
  announcements, readIds, open, onClose, onMarkRead, onMarkAllRead,
}: Props) {
  // Sélection initiale = première non-lue, sinon la plus récente.
  const firstUnread = announcements.find((a) => !readIds.has(a.id));
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected =
    announcements.find((a) => a.id === selectedId) ?? firstUnread ?? announcements[0];

  // Reset de la sélection à chaque ouverture (retomber sur la 1re non-lue).
  useEffect(() => { if (open) setSelectedId(null); }, [open]);

  const unreadCount = announcements.filter((a) => !readIds.has(a.id)).length;

  const select = (a: Announcement) => {
    setSelectedId(a.id);
    if (!readIds.has(a.id)) onMarkRead(a.id);
  };

  if (!selected) return null; // catalogue vide — rien à afficher

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent hideCloseButton
        className="max-w-[1240px] w-[94vw] h-[88vh] max-h-[88vh] p-0 gap-0 flex flex-row overflow-hidden rounded-[14px]">
        <DialogTitle className="sr-only">Nouveautés Hector</DialogTitle>

        {/* ── Mini-sidebar : liste des annonces ───────────────────────── */}
        <nav aria-label="Liste des annonces"
          className="w-[264px] flex-shrink-0 flex flex-col border-r border-grey-100 bg-grey-50">
          <div className="px-5 pt-[20px] pb-[12px]">
            <div className="text-[15px] font-semibold text-charcoal">Nouveautés</div>
            <div className="text-[11.5px] text-grey-400 mt-[1px]">
              {unreadCount > 0
                ? `${unreadCount} ${unreadCount > 1 ? 'annonces non lues' : 'annonce non lue'}`
                : 'Vous êtes à jour'}
            </div>
          </div>

          <ul className="flex-1 overflow-auto px-2.5 pb-2 space-y-[5px]">
            {announcements.map((a) => {
              const isRead = readIds.has(a.id);
              const isActive = a.id === selected.id;
              return (
                <li key={a.id}>
                  <button type="button" onClick={() => select(a)}
                    aria-current={isActive ? 'page' : undefined}
                    className={`w-full text-left rounded-[9px] px-3 py-[9px] border transition-colors
                      ${isActive
                        ? 'bg-white border-[#e8d9a8] shadow-sm'
                        : 'border-transparent hover:bg-white hover:border-grey-100'}`}>
                    <span className={`block text-[9px] font-bold uppercase tracking-[0.1em] mb-[3px]
                      ${!isRead ? 'text-[#8a6400]' : 'text-grey-400'}`}>
                      {a.kicker}
                    </span>
                    <span className="flex items-start gap-2">
                      <span className={`flex-1 text-[12.5px] leading-[1.3]
                        ${!isRead ? 'font-semibold text-charcoal' : 'font-medium text-grey-600'}`}>
                        {a.navLabel ?? a.title}
                      </span>
                      {!isRead && (
                        <span aria-label="Non lu"
                          className="mt-[4px] h-2 w-2 flex-shrink-0 rounded-full bg-[#B8860B]" />
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>

          <div className="px-5 py-[10px] border-t border-grey-100">
            <button type="button" onClick={onMarkAllRead} disabled={unreadCount === 0}
              className="text-[11.5px] font-semibold text-[#1f3d6b] hover:underline
                         disabled:text-grey-400 disabled:no-underline disabled:cursor-default">
              Tout marquer comme lu
            </button>
          </div>
        </nav>

        {/* ── Volet droit : contenu de l'annonce sélectionnée ─────────── */}
        <div className="relative flex-1 min-w-0 flex flex-col">
          <button onClick={onClose} aria-label="Fermer"
            className="absolute top-4 right-[18px] h-7 w-7 flex items-center justify-center
                       text-[24px] leading-none text-grey-400 hover:text-charcoal transition-colors z-10">
            &times;
          </button>
          {/* key = remount au changement d'annonce → reset état local du contenu */}
          <AnnouncementContent key={selected.id} announcement={selected} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
